import { useEffect, useState } from "react";

import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Box, IconButton } from "@mui/material";

import { Device } from "../../../../../../API";
import { useStream } from "../../../../../../common/hooks/useStream";
import { extractDeviceNameFromId } from "../../../../../../common/utils/getEntityName";
import { selectedStreamObjectVariable } from "../../../../../../common/variables/selectedStreamObject";
import { usePublishNode } from "../../../../../devices/hooks/usePublishNode";
import S3Image from "../../../../../../common/components/media/S3Image";

interface ClickToPlayHLSStreamProps {
  device: Device;
  onStarted?: () => void;
  onSize?: (size: { width: number; height: number }) => void;
  fixedHeight?: string;
  onAspectRatio?: (ratio: number) => void;
}

const ClickToPlayHLSStream = ({
  device,
  onStarted,
  onSize,
  fixedHeight,
  onAspectRatio,
}: ClickToPlayHLSStreamProps) => {
  const { getStreamName } = useStream();
  const { publishNode } = usePublishNode();

  const [starting, setStarting] = useState(false);
  const [imageAspectRatio, setImageAspectRatio] = useState<
    number | undefined
  >();

  const handleStart = (): void => {
    if (!device || starting) return;

    setStarting(true);

    const { extractedDeviceName } = extractDeviceNameFromId(device.id);
    const streamName = getStreamName(device.nodeId, device.id);

    publishNode({
      message: JSON.stringify({
        TARGET: "KINESIS_STREAM",
        ACTION: "KINESIS_STREAM_START",
        streamName,
        deviceName: extractedDeviceName,
      }),
      nodeId: device.nodeId,
    })
      .then((): void => {
        selectedStreamObjectVariable({
          streamName,
          extractedDeviceName,
          nodeId: device.nodeId,
        });

        if (onStarted) onStarted();
      })
      .catch((): void => {
        console.error("Failed to start live stream");
      })
      .finally(() => setStarting(false));
  };

  // Preload to get natural image size and notify parent
  const s3Key = device?.deviceData?.signedUrlRecentFrame ?? "";
  const handleMeasure = (): void => {
    if (!s3Key) return;

    const img = new Image();

    img.onload = () => {
      if (onSize) {
        onSize({ width: img.naturalWidth, height: img.naturalHeight });
      }

      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        const ratio = img.naturalWidth / img.naturalHeight;

        setImageAspectRatio(ratio);

        if (onAspectRatio) onAspectRatio(ratio);
      }
    };

    img.src = s3Key;
  };

  useEffect(() => {
    handleMeasure();
  }, [s3Key, onSize]);

  return (
    <Box
      sx={{
        width: "100%",
        height: fixedHeight ?? "220px",
        position: "relative",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "#000",
      }}
    >
      <S3Image
        s3Key={s3Key}
        sx={{
          width: "100%",
          height: "100%",
          maxHeight: fixedHeight ?? "220px",
          borderRadius: "8px",
          objectFit: "contain",
          objectPosition: "center",
          aspectRatio: imageAspectRatio ?? "auto",
          backgroundColor: "#000",
        }}
        keyFrameMessage="Please wait for the image to retrieve"
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <IconButton
          onClick={handleStart}
          color="primary"
          sx={{
            width: 64,
            height: 64,
            backgroundColor: "rgba(0,0,0,0.5)",

            borderRadius: "50%",

            "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
          }}
          disabled={starting}
        >
          <PlayArrowIcon sx={{ color: "#fff", fontSize: 36 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ClickToPlayHLSStream;
